import { useAuth0 } from "@auth0/auth0-react";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { alpha, Autocomplete, Divider, styled, Typography } from "@mui/material";
import { payloadSlateToHtmlConfig, slateToHtml } from "@slate-serializers/html";
import { useFeatureGate } from "@statsig/react-bindings";
import { useMemo, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import { withReact } from "slate-react";

import { Box } from "@/Components/v2/atoms/Box";
import { Stack } from "@/Components/v2/atoms/Stack/";
import { TabContext, TabListV2, TabV2 } from "@/Components/v2/atoms/Tabs";
import { Tag } from "@/Components/v2/atoms/Tag/Tag";
import { TextField } from "@/Components/v2/atoms/TextField";
import ErrorFallback from "@/Components/v2/molecule/ErrorFallback/ErrorFallback";
import ActivityLogDrawer from "@/Components/v2/organism/Drawer/ActivityLogDrawer";
import RichTextEditor from "@/Components/v2/organism/RichTextEditor/RichTextEditor";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";
import { ALL_GEOGRAPHIES_LABEL, EN_DASH } from "@/pages/scenario-builder/constants";
import { formatDate } from "@/pages/scenario-builder/utils";
import { clearAndResetEditor } from "@/pages/scenario-builder/utils/clearAndResetEditor";
import { isEditorContentEmpty } from "@/pages/scenario-builder/utils/isEditorContentEmpty";
import { useCreateComment } from "@/pages/scenario-builder/v3/api/ActivityLog/createComment";
import ScenarioActionButtons from "@/pages/scenario-builder/v3/components/ScenarioActionButtons";
import ScenarioActivityFeed from "@/pages/scenario-builder/v3/components/ScenarioActivityFeed";
import ScenarioApprovalsNavigationBar from "@/pages/scenario-builder/v3/components/ScenarioApprovalsNavigationBar";
import {
  ScenarioType,
  ScenarioTypeEnum,
  StatusEnum,
} from "@/pages/scenario-builder/v3/pages/scenario-form/types";

import { useGeoEnabled } from "../../../hooks/useGeoEnabled";
import { getLikelihoodLevel } from "../helpers/likelihood";
import MetaField from "@/Components/v2/molecule/MetaField/MetaField";
import { isNil } from "lodash";

interface ScenarioResultsHeaderProps {
  scenarioId: string;
  scenarioName: string;
  scenarioDescription: string;
  scenarioStatus: StatusEnum;
  brand: string;
  startDate: string;
  endDate: string;
  scenarioType: ScenarioTypeEnum;
  scenarioCount?: number;
  geography?: string;
  availableGeographies?: string[];
  onGeographyChange?: (geography: string | null) => void;
  handleUpdateScenario: (updatedScenarioStatus: StatusEnum) => void;
  likelihoodPercentage?: number | null;
}

type GeographyOption = {
  isAllGeos: boolean;
  value: string;
};

const LikelihoodTabLabel: React.FC<{ likelihoodPercentage: number | undefined }> = ({
  likelihoodPercentage,
}) => {
  if (likelihoodPercentage === undefined) return null;
  const likelihoodLevel = getLikelihoodLevel(likelihoodPercentage);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <span>Likelihood</span>
      <Tag
        label={`${likelihoodPercentage}%`}
        size="small"
        sx={{
          backgroundColor: alpha(likelihoodLevel.color, 0.1),
          color: likelihoodLevel.color,
          fontSize: "1rem",
          fontWeight: 400,
          height: "24px",
          "& .MuiChip-label": {
            padding: "8px",
          },
        }}
      />
    </Stack>
  );
};
export const ScenarioResultsHeader = ({
  scenarioId,
  scenarioName,
  scenarioDescription,
  scenarioStatus,
  brand,
  startDate,
  endDate,
  scenarioType,
  scenarioCount,
  geography,
  availableGeographies = [],
  onGeographyChange,
  handleUpdateScenario,
  likelihoodPercentage,
}: ScenarioResultsHeaderProps) => {
  const location = useLocation();
  const history = useHistory();
  const { getBCPHierarchyLabel } = useDefaultNodeNameInfo();
  const { value: isScenariosApiEnabled } = useFeatureGate(featureFlag.scenarioBuilderApi);

  // Activity log state management
  const ACTIVITY_LOG_DRAWER_WIDTH = 550;
  const [isActivityLogOpen, setIsActivityLogOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  const initialEditorValue =
    editor.children.length > 0 ? editor.children : [{ children: [{ text: "" }] }];

  // Get user data from Redux store
  const { user } = useAuth0();

  // Comment creation hook
  const { mutate: createComment } = useCreateComment({
    scenarioId: scenarioId,
    isScenariosApiEnabled,
  });

  // Comment creation handler
  const handleCreateComment = () => {
    const commentData = editor.children;
    const htmlContent = slateToHtml(commentData, payloadSlateToHtmlConfig);

    if (isEditorContentEmpty(editor)) {
      toast.error("Comment cannot be empty");
      return;
    }

    if (!scenarioId) return;

    createComment({
      scenarioId: scenarioId,
      commentData: htmlContent,
    });

    clearAndResetEditor(editor);
    setIsExpanded(false);
  };

  // Scenario status update handler - delegate to parent to avoid double API calls
  const handleScenarioStatusUpdate = (updatedScenarioStatus: StatusEnum) => {
    if (!scenarioId) return;

    // Only call the parent handler, which will handle the API call
    handleUpdateScenario(updatedScenarioStatus);
  };

  // ScenarioSummary inline logic
  const geoEnabledQuery = useGeoEnabled();
  const isGeoEnabled = geoEnabledQuery.isLoading ? false : geoEnabledQuery.isEnabled;

  const geographyValue = isGeoEnabled ? (geography ?? ALL_GEOGRAPHIES_LABEL) : (geography ?? "-");

  // Only show dropdown if geography is enabled, we can change it, and there are multiple meaningful choices
  // With "All Geographies" + 1 geography option = 2 choices, so show dropdown
  // With only 1 geography option = only 1 real choice, so show static field
  const shouldShowGeographyDropdown =
    isGeoEnabled && onGeographyChange && availableGeographies.length > 1;

  // If there's only one geography option, show it statically
  const singleGeographyValue = availableGeographies.length === 1 ? availableGeographies[0] : null;

  const customerGeographiesWithAllGeos: GeographyOption[] = [
    { isAllGeos: true, value: ALL_GEOGRAPHIES_LABEL },
    ...availableGeographies.map((geo) => ({ isAllGeos: false, value: geo })),
  ];

  const currentGeography: GeographyOption = geography
    ? { isAllGeos: false, value: geography }
    : { isAllGeos: true, value: ALL_GEOGRAPHIES_LABEL };

  const handleGeographyDropdownChange = (
    _event: React.SyntheticEvent,
    value: GeographyOption | null
  ) => {
    if (onGeographyChange && value) {
      const newGeography = value.isAllGeos ? null : value.value;
      onGeographyChange(newGeography);
    }
  };

  const brandLabel = getBCPHierarchyLabel({
    brand,
    category: "default",
    product: "default",
  });

  const { value: isRiskEnabled } = useFeatureGate(featureFlag.scenarioBuilderRisk);

  const isActive = (value: string) => {
    return location.pathname.includes(value);
  };

  const tabs = [
    { label: "Summary", value: "results", active: isActive("results") },
    { label: "Inputs", value: "inputs", active: isActive("inputs") },
    ...(isRiskEnabled && !isNil(likelihoodPercentage)
      ? [
          {
            value: "likelihood",
            active: isActive("likelihood"),
            label: <LikelihoodTabLabel likelihoodPercentage={likelihoodPercentage} />,
          },
        ]
      : []),
  ];

  const currentTab = tabs.find((tab) => tab.active);

  return (
    <Box>
      <ScenarioApprovalsNavigationBar
        scenarioId={scenarioId}
        scenarioName={scenarioName}
        scenarioDescription={scenarioDescription}
        scenarioStatus={scenarioStatus}
        isActivityLogOpen={isActivityLogOpen}
        setIsActivityLogOpen={setIsActivityLogOpen}
        handleUpdateScenario={handleScenarioStatusUpdate}
      />
      <Divider sx={{ marginBottom: "1.5rem" }} />

      {/* ScenarioSummary inline */}
      <Stack direction="row" spacing={2} borderRadius={4}>
        <MetaField value={ScenarioType.toString(scenarioType, scenarioCount)} />

        <MetaField
          value={`${formatDate(new Date(startDate))} ${EN_DASH} ${formatDate(new Date(endDate))}`}
        />
        <MetaField value={`Portfolio: ${brandLabel}`} />
        {isGeoEnabled && (
          <>
            {shouldShowGeographyDropdown ? (
              <Stack direction="column" spacing={1} px={1} mx={1} borderRadius={4} width="15rem">
                <Autocomplete<GeographyOption | null, false, true>
                  value={currentGeography}
                  getOptionLabel={(option) => option?.value ?? ""}
                  options={customerGeographiesWithAllGeos}
                  onChange={handleGeographyDropdownChange}
                  disabled={false}
                  disableClearable
                  size="small"
                  isOptionEqualToValue={(option, value) =>
                    option?.isAllGeos === value?.isAllGeos && option?.value === value?.value
                  }
                  renderInput={(params) => <TextField {...params} placeholder="Select Geography" />}
                  renderOption={(props, option) => {
                    if (!option) {
                      return null;
                    }

                    if (option.isAllGeos) {
                      return (
                        <>
                          <Typography {...props}>{option.value}</Typography>
                          <Divider sx={{ marginY: "0.5rem" }} />
                        </>
                      );
                    }
                    return <Typography {...props}>{option.value}</Typography>;
                  }}
                />
              </Stack>
            ) : (
              <MetaField value={singleGeographyValue || geographyValue} />
            )}
          </>
        )}
      </Stack>

      <TabContext value={currentTab?.value ?? ""}>
        <TabContainer>
          <TabListV2 aria-label="scenario navigation tabs">
            {tabs.map((tab) => (
              <TabV2
                key={tab.value}
                label={tab.label}
                value={tab.value}
                onClick={() => history.push(tab.value)}
              />
            ))}
          </TabListV2>
        </TabContainer>
      </TabContext>

      {/* Activity Log Drawer - Always Enabled */}
      <ActivityLogDrawer
        anchor="right"
        open={isActivityLogOpen}
        onClose={() => setIsActivityLogOpen(false)}
        sx={{
          width: ACTIVITY_LOG_DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: ACTIVITY_LOG_DRAWER_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        <Box>
          <Stack direction="row" justifyContent="space-between" sx={{ padding: 2 }}>
            <KeyboardDoubleArrowRightIcon
              cursor="pointer"
              fontSize="large"
              onClick={() => setIsActivityLogOpen(false)}
            />
            <ScenarioActionButtons
              scenarioName={scenarioName}
              scenarioId={scenarioId}
              isActivityLogOpen={isActivityLogOpen}
              setIsActivityLogOpen={setIsActivityLogOpen}
              scenarioStatus={scenarioStatus}
              handleUpdateScenario={handleScenarioStatusUpdate}
            />
          </Stack>
          <ErrorBoundary fallback={<ErrorFallback />}>
            {/* Comment Section - Always Enabled */}
            <Box sx={{ paddingX: 2, paddingBottom: 2 }}>
              {!isExpanded ? (
                <Box>
                  <Typography variant="body2" fontWeight={600} paddingBottom={1}>
                    Write a comment
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Add a comment"
                    onFocus={() => setIsExpanded(true)}
                    variant="outlined"
                  />
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" fontWeight={600} paddingBottom={1}>
                    Add a comment
                  </Typography>
                  <RichTextEditor
                    editor={editor}
                    initialValue={initialEditorValue}
                    toolbarElements={["bold", "italic", "underline"]}
                    onSubmit={handleCreateComment}
                    maxCharacters={1000}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                height: "calc(100vh - 64px)",
                paddingBottom: "200px",
              }}
            >
              <ScenarioActivityFeed
                scenarioId={scenarioId}
                scenarioName={scenarioName}
                email={user?.email}
                isScenariosApiEnabled={isScenariosApiEnabled}
              />
            </Box>
          </ErrorBoundary>
        </Box>
      </ActivityLogDrawer>
    </Box>
  );
};

const TabContainer = styled("div")(() => {
  return {
    borderBottom: "1px #EFEFEF solid",
    borderColor: "divider",
    width: "95%",
  };
});
